/* eslint-disable react/no-danger */
import React from "react";
import { ActivityLevel, TravelStyleDetail } from "@/types/dto/pdp/pdp-header-about";
import { TourSummary } from "@/types/dto/pdp/pdp-tour-summary";
import { Box, Stack } from "@mui/material";
import OverviewDetails from "./overview-details";
import EssentialTour from "./essential-tour";
import TourOverviewSkeleton from "./tour-overview-skeleton";
import InclusionsFeaturedExperiences from "./inclusions-featured-experiences";

interface TourSummaryProps {
  tourSummaryData?: TourSummary,
  phoneNumber?: string,
  activityLevels: ActivityLevel[],
  travelStyles: TravelStyleDetail[],
  isLoading?: boolean
}

const TourOverview = ({ 
  tourSummaryData, 
  phoneNumber = "",
  activityLevels,
  travelStyles,
  isLoading = false 
}: TourSummaryProps) => {
  
  // Show skeleton while loading or when data is not available
  if (isLoading || !tourSummaryData) {
    return <TourOverviewSkeleton />;
  }

  const activityLevelDetails = activityLevels.find(activityLevel => activityLevel.number === Number(tourSummaryData?.activityLevel));

  // Extract first 2 sentences from activity level description
  const activityLevelShortDescription = activityLevelDetails?.description
    ? activityLevelDetails.description.split(".").slice(0, 1).join(".").trim().concat(".") : "";

  const activityLevelPhysicalRequirement = activityLevelDetails?.description
    ? activityLevelDetails.description.split(".").slice(2, 3).join(".").trim().concat(".") : "";

  const travelStyleDetails = tourSummaryData?.travelStyles?.map(style =>
    travelStyles.find(travelStyle => travelStyle.name === style)
  ).filter(Boolean) || [];

  const travelStyleNames = travelStyleDetails.map(style => style?.name).join(", ");
  const travelStyleDescriptions = travelStyleDetails.map(style => style?.description).join(" ");

  return (
    <Stack 
      component="section" 
      spacing={4}
      data-testid="tour-overview-section-container"
      data-track="visibility"
      data-module-name="Tour Overview"
      data-parent-container-type="pdp"
      data-ranking-algorithm="manual"
      data-module-ranking-position="5"
      id="tour-overview"
      sx={{ scrollMarginTop: 160 }}

    >

      {/* Two Column Layout */}
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row"} }}>
        {/* Left Column: Overview Details Card */}
        <OverviewDetails tourSummaryData={tourSummaryData} />

        {/* Right Column: 35% */}
        <Box
          sx={{
            flex: { xs: "1 1 100%", md: "0 0 35%" },
            px: { xs: "2.4rem", md: "4rem" },
            py: "4rem"
          }}
        >
          <EssentialTour
            tourSummaryData={tourSummaryData}
            activityLevelShortDescription={activityLevelShortDescription}
            activityLevelPhysicalRequirement={activityLevelPhysicalRequirement}
            travelStyleNames={travelStyleNames}
            travelStyleDescriptions={travelStyleDescriptions}
            phoneNumber={phoneNumber}
          />
        </Box>
      </Box>

      <InclusionsFeaturedExperiences
        totalMeals={tourSummaryData.totalMeals}
        totalBreakfasts={tourSummaryData.totalBreakfasts}
        totalLunches={tourSummaryData.totalLunches}
        totalDinners={tourSummaryData.totalDinners}
        accommodationCount={tourSummaryData.accommodationCount}
        cultural={tourSummaryData.cultural}
        culinary={tourSummaryData.culinary}
        highlights={tourSummaryData.highlights}
        mustSee={tourSummaryData.mustSee}
        headingTestId="tour-overview-inclusions-heading"
        customSx={
          { display: { xs: "flex", md: "none" }, 
          p: {xs: "2.4rem", sm: "4rem 3.2rem"}, 
          m:"0 !important",
          bgcolor: "background.warm_strong",
          position: "relative",
          "&::before": {
            content: "\"\"",
            position: "absolute",
            top: 0,
            bottom: 0,
            right: "100%",
            width: "100vw",
            bgcolor: "background.warm_strong"
          }           
          }
        }
      />      
    </Stack>
  );
};

export default TourOverview;